import { createFileRoute, Link, useNavigate, redirect } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Camera, Check, ChevronRight, Loader2, MapPin, User as UserIcon } from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { getMyProfile, updateMyProfile, uploadMyAvatar } from "@/lib/profile.functions";
import { compressImage, LEGACY_OUTPUT_TYPES, IMAGE_ACCEPT_ATTR } from "@/lib/compress-image";

type CityOption = { slug: string; name: string; region: string | null };

function OnboardingPage() {
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);

  const [userId, setUserId] = useState<string | null>(null);
  const [email, setEmail] = useState<string>("");

  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [displayName, setDisplayName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [dob, setDob] = useState("");
  const [city, setCity] = useState("");
  const [cities, setCities] = useState<CityOption[]>([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Resolve session + prefill + load cities
  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (!data.user) {
        navigate({ to: "/login" });
        return;
      }
      setUserId(data.user.id);
      setEmail(data.user.email ?? "");
      const meta = (data.user.user_metadata ?? {}) as Record<string, unknown>;
      const nm =
        (meta.full_name as string | undefined) ||
        (meta.name as string | undefined) ||
        (data.user.email?.split("@")[0] ?? "");
      setDisplayName(nm);
      const av = (meta.avatar_url as string | undefined) || (meta.picture as string | undefined);
      if (av) setAvatarPreview(av);

      try {
        const existing = await getMyProfile();
        if (existing) {
          if (existing.display_name) setDisplayName(existing.display_name);
          // Preview only — avatar_url here is a signed URL. We only submit a
          // new avatar via uploadMyAvatar (which sets avatarUrl to the path).
          if (existing.avatar_url) setAvatarPreview(existing.avatar_url);
          if (existing.home_city) setCity(existing.home_city);
          if (existing.date_of_birth) setDob(existing.date_of_birth);
        }
      } catch {
        // non-fatal — onboarding still works without prefill
      }
    })();

    (async () => {
      const { data } = await supabase
        .from("cities")
        .select("slug, name, region")
        .eq("active", true)
        .order("sort_order", { ascending: true })
        .order("name", { ascending: true });
      setCities((data ?? []) as CityOption[]);
    })();
  }, [navigate]);

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !userId) return;
    setError(null);
    setUploading(true);
    try {
      // Strict validation (real image bytes + size cap) then resize to 1024px
      // and squeeze under 100 KB, same as the admin pipeline.
      const compressed = await compressImage(file, 100 * 1024, 1024, {
        outputTypes: LEGACY_OUTPUT_TYPES,
        maxInputBytes: 15 * 1024 * 1024,
      });
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ""));
        reader.onerror = () => reject(new Error("Failed to read image"));
        reader.readAsDataURL(compressed);
      });
      const uploaded = await uploadMyAvatar({
        data: {
          fileName: compressed.name,
          mimeType: compressed.type as "image/jpeg" | "image/webp",
          dataUrl,
        },
      });
      setAvatarUrl(uploaded.path);
      setAvatarPreview(uploaded.signedUrl ?? uploaded.path);
    } catch (err) {
      setError(err instanceof Error ? err.message : "upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  async function handleFinish() {
    setError(null);
    setSaving(true);
    try {
      await updateMyProfile({
        data: {
          display_name: displayName.trim() || null,
          avatar_url: avatarUrl ?? undefined,
          date_of_birth: dob || null,
          home_city: city || null,
          accept_terms: acceptTerms,
        },
      });
      navigate({ to: "/profile" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "could not save profile");
    } finally {
      setSaving(false);
    }
  }

  const initials = (displayName || email || "?")
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const today = new Date();
  const maxDob = new Date(today.getFullYear() - 13, today.getMonth(), today.getDate())
    .toISOString()
    .split("T")[0];
  const minDob = "1900-01-01";

  return (
    <div className="min-h-screen bg-gradient-to-br from-[hsl(190_55%_94%)] via-white to-[hsl(190_45%_92%)] px-4 py-10">
      <div className="mx-auto max-w-2xl">
        <div className="mb-6 flex items-center justify-between">
          <Link to="/" className="text-xs text-muted-foreground hover:text-foreground">
            ← back to site
          </Link>
          <button
            onClick={async () => {
              await supabase.auth.signOut();
              navigate({ to: "/login" });
            }}
            className="text-xs text-muted-foreground hover:text-foreground underline"
          >
            sign out
          </button>
        </div>

        {/* Stepper */}
        <div className="mb-6 flex items-center gap-2">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className="flex-1">
              <div
                className={`h-1.5 rounded-full transition-colors ${
                  n <= step ? "bg-[#0b1d3a]" : "bg-[#e5e7eb]"
                }`}
              />
              <p
                className={`mt-2 text-[11px] uppercase tracking-wide ${
                  n <= step ? "text-[#0b1d3a] font-semibold" : "text-muted-foreground"
                }`}
              >
                {n === 1 ? "your photo" : n === 2 ? "about you" : n === 3 ? "your city" : "terms"}
              </p>
            </div>
          ))}
        </div>

        <Card className="p-8" style={{ fontFamily: "Poppins, sans-serif" }}>
          <h1 className="text-2xl font-bold text-[#0b1d3a]">
            {step === 1 && "add a profile photo"}
            {step === 2 && "tell us a bit about you"}
            {step === 3 && "where do you call home?"}
            {step === 4 && "agree to our terms"}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {step === 1 && "a friendly face makes your profile pop."}
            {step === 2 && "your name and date of birth — used for personalisation."}
            {step === 3 && "pick your home city so we can surface what's near you."}
            {step === 4 && "please review and accept before continuing."}
          </p>

          <div className="mt-8 space-y-6">
            {step === 1 && (
              <div className="flex flex-col items-center gap-5 py-4">
                <div className="relative">
                  <div className="size-32 rounded-full overflow-hidden ring-4 ring-white shadow-lg bg-gradient-to-br from-[#0b1d3a] to-[#1e3a8a] flex items-center justify-center text-white text-3xl font-bold">
                    {avatarPreview ? (
                      <img src={avatarPreview} alt="avatar" className="size-full object-cover" />
                    ) : (
                      <span>{initials}</span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="absolute bottom-0 right-0 size-10 rounded-full bg-[#0b1d3a] text-white shadow-lg flex items-center justify-center hover:bg-[#13294e] transition disabled:opacity-50"
                    aria-label="upload photo"
                  >
                    {uploading ? <Loader2 className="size-4 animate-spin" /> : <Camera className="size-4" />}
                  </button>
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept={IMAGE_ACCEPT_ATTR}
                  className="hidden"
                  onChange={handleFile}
                />
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="text-sm font-medium text-[#0b1d3a] underline hover:no-underline"
                  >
                    {avatarPreview ? "replace photo" : "upload a photo"}
                  </button>
                  <p className="mt-1 text-xs text-muted-foreground">jpg, png or webp, up to 5 mb</p>
                </div>
              </div>
            )}

            {step === 2 && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="name">display name</Label>
                  <div className="relative">
                    <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                    <Input
                      id="name"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      placeholder="how should we call you?"
                      className="pl-9"
                      maxLength={80}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dob">date of birth</Label>
                  <Input
                    id="dob"
                    type="date"
                    value={dob}
                    onChange={(e) => setDob(e.target.value)}
                    min={minDob}
                    max={maxDob}
                  />
                  <p className="text-xs text-muted-foreground">
                    you must be at least 13 years old.
                  </p>
                </div>
              </>
            )}

            {step === 3 && (
              <div className="space-y-3">
                <Label>home city</Label>
                {cities.length === 0 ? (
                  <div className="space-y-2">
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                      <Input
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        placeholder="e.g. helsinki"
                        className="pl-9"
                        maxLength={80}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {cities.map((c) => {
                      const selected = city === c.slug;
                      return (
                        <button
                          key={c.slug}
                          type="button"
                          onClick={() => setCity(c.slug)}
                          className={`relative rounded-lg border p-3 text-left transition ${
                            selected
                              ? "border-[#0b1d3a] bg-[#0b1d3a]/5 ring-2 ring-[#0b1d3a]/20"
                              : "border-[#e5e7eb] hover:border-[#0b1d3a]/40 hover:bg-muted/40"
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <MapPin className="size-4 text-[#0b1d3a]" />
                            <span className="text-sm font-semibold capitalize text-[#0b1d3a]">
                              {c.name}
                            </span>
                          </div>
                          {c.region && (
                            <p className="mt-1 text-[11px] text-muted-foreground capitalize">
                              {c.region}
                            </p>
                          )}
                          {selected && (
                            <Check className="absolute top-2 right-2 size-4 text-[#0b1d3a]" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">you can change this anytime in your profile.</p>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-4">
                <div className="max-h-64 overflow-y-auto rounded-lg border border-[#e5e7eb] bg-muted/30 p-4 text-sm text-muted-foreground leading-relaxed">
                  <p className="font-semibold text-[#0b1d3a] mb-2">terms of service & privacy</p>
                  <p>
                    by creating an account you agree that what finland may store your profile
                    details, preferences and activity to personalise the experience. you remain
                    in control: you can update or delete your data anytime from your profile.
                  </p>
                  <p className="mt-2">
                    we never sell your personal data. by continuing you confirm you have read
                    and accept our terms of service and privacy policy.
                  </p>
                </div>
                <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-[#e5e7eb] p-3 hover:bg-muted/40 transition">
                  <input
                    type="checkbox"
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    className="mt-1 size-4 accent-[#0b1d3a]"
                  />
                  <span className="text-sm text-[#0b1d3a]">
                    i have read and agree to the terms of service and privacy policy.
                  </span>
                </label>
              </div>
            )}

            {error && <p className="text-sm text-destructive">{error}</p>}

            <div className="flex items-center justify-between gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setStep((s) => (s > 1 ? ((s - 1) as 1 | 2 | 3 | 4) : s))}
                disabled={step === 1}
              >
                back
              </Button>
              {step < 4 ? (
                <Button
                  type="button"
                  onClick={() => setStep((s) => (s + 1) as 1 | 2 | 3 | 4)}
                  disabled={(step === 1 && uploading) || (step === 2 && (!displayName.trim() || !dob || dob < minDob || dob > maxDob))}
                  className="gap-1"
                >
                  continue <ChevronRight className="size-4" />
                </Button>
              ) : (
                <Button type="button" onClick={handleFinish} disabled={saving || !acceptTerms} className="gap-1">
                  {saving ? (
                    <>
                      <Loader2 className="size-4 animate-spin" /> saving…
                    </>
                  ) : (
                    <>
                      finish <Check className="size-4" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/onboarding")({
  beforeLoad: async () => {
    const { data } = await supabase.auth.getUser();
    if (!data.user) throw redirect({ to: "/login" });

    // Returning users who've already accepted terms skip straight to profile.
    const { data: profile } = await supabase
      .from("profiles")
      .select("terms_accepted_at")
      .eq("user_id", data.user.id)
      .maybeSingle();
    if (profile?.terms_accepted_at) {
      throw redirect({ to: "/profile" });
    }
  },
  head: () => ({
    meta: [
      { title: "Welcome — what Finland" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: OnboardingPage,
});
